import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../style/DashboardPage.module.css';
import { useAuth } from '../context/AuthContext';
import { useTutorial } from '../context/TutorialContext';
import { getUserProfile } from '../services/authService';
import supabase from '../supabaseClient';
import ReportViewerModal from '../components/ReportViewerModal';
import UpgradePrompt from '../components/UpgradePrompt';
import axios from 'axios'; 

import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

import * as XLSX from 'xlsx';

import HistoryIcon from '@mui/icons-material/History';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

// --- STATIC TRANSLATIONS (AWN DATA) ---
const STATIC_SENSOR_TRANSLATIONS = { 
    "IW1": "Wasserablassventil links", "IW2": "Wasserablassventil rechts", "IW3": "Speisewassertank Füllstand", 
    "IW4": "Speisewassertank Temperatur", "IW5": "Speisewasserpumpe Sollwert FU", "IW6": "Speisewasser Druck vor Pumpe", 
    "IW7": "VDL Speisewasserventil Stellung", "IW8": "VDL Abgaseintrittstemperatur", "IW9": "VDL Abgasaustrittstemperatur", 
    "IW10": "VDL Abgasgegendruck", "IW11": "VDL Dampfaustrittstemperatur", "IW12": "VDL_x Speisewasserventil Stellung", 
    "IW13": "VDL_x Abgaseintrittstemperatur", "IW14": "VDL_x Abgasaustrittstemperatur", "IW15": "VDL_x Abgasgegendruck", 
    "IW16": "VDL_x Dampfaustrittstemperatur", "IW17": "VDR Speisewasserventil Stellung", "IW18": "VDR Abgaseintrittstemperatur", 
    "IW19": "VDR Abgasaustrittstemperatur", "IW20": "VDR Abgasgegendruck", "IW21": "VDR Dampfaustrittstemperatur", 
    "IW22": "Generatorleistung", "IW23": "Dampftemperatur vor Expander", "IW24": "Dampfdruck vor Expander", 
    "IW25": "Öldruck Expander", "IW26": "Öltemperatur Expander", "IW27": "Expander läuft", "IW28": "Generatordrehzahl", 
    "IW29": "Kühlkreis Kondensatoraustrittstemperatur", "IW30": "Sollwert Leistungsregelung Dampf", 
    "IW31": "VDR_x Speisewasserventil Stellung", "IW32": "VDR_x Abgaseintrittstemperatur", "IW33": "VDR_x Abgasaustrittstemperatur", 
    "IW34": "VDR_x Abgasgegendruck", "IW35": "VDR_x Dampfaustrittstemperatur", "IW36": "Öl Schauglastemperatur", 
    "IW37": "Ölpumpe läuft", "IW38": "VDR Abgas Steilheit", "IW39": "VDL Abgas Steilheit", "IW40": "VDL_x Abgas Steilheit", 
    "IW41": "VDR_x Abgas Steilheit", "IW42": "Dampf vor Expander Temp.Mittelwert_x_Zykl" 
};

// --- HELPER: Filter out unwanted calculated columns ---
const isAllowedColumn = (colName) => {
    if (!colName) return false;
    const lower = colName.toLowerCase();
    return lower !== 'calc_oel' && lower !== 'calc_wasser' && lower !== 'calc_öl';
};

// --- COMPONENT: Sensor Renaming Modal ---
function SensorRenamingModal({ isOpen, onClose, currentData, mappings, onSave }) {
    const [tempMappings, setTempMappings] = useState(mappings);
    const [availableSensors, setAvailableSensors] = useState([]);

    useEffect(() => {
        if (isOpen && currentData) {
            setTempMappings(mappings);
            const keys = new Set();
            
            // Extract from Daily
            if (currentData.daily) {
                currentData.daily.forEach(row => {
                    Object.keys(row).forEach(k => {
                        if (k !== 'Filename' && k !== 'report_date' && isAllowedColumn(k)) keys.add(k);
                    });
                });
            }
            // Extract from Monthly
            if (currentData.monthly) {
                Object.values(currentData.monthly).forEach(rows => {
                    rows.forEach(row => {
                         Object.keys(row).forEach(k => {
                            if (k !== 'Day' && isAllowedColumn(k)) keys.add(k);
                         });
                    });
                });
            }
            setAvailableSensors(Array.from(keys).sort());
        }
    }, [isOpen, currentData, mappings]);

    const handleChange = (originalName, newName) => {
        setTempMappings(prev => ({ ...prev, [originalName]: newName }));
    };

    const handleSave = () => {
        onSave(tempMappings);
    };

    const getPlaceholder = (sensor) => {
        return STATIC_SENSOR_TRANSLATIONS[sensor] || sensor;
    }

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2>Sensoren umbenennen</h2>
                <button onClick={onClose} className={styles.modalCloseButton}>
                    <CloseIcon fontSize="inherit" />
                </button>
                <div style={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: '1rem' }}>
                    <table className={styles.reportTable}>
                        <thead>
                            <tr>
                                <th>Original-ID</th>
                                <th>Anzeigename</th>
                            </tr>
                        </thead>
                        <tbody>
                            {availableSensors.map(sensor => (
                                <tr key={sensor}>
                                    <td>
                                        {sensor} <br/>
                                        <small style={{ color: 'var(--text-color-light)' }}>{STATIC_SENSOR_TRANSLATIONS[sensor]}</small>
                                    </td>
                                    <td>
                                        <input 
                                            type="text" 
                                            value={tempMappings[sensor] || ''} 
                                            placeholder={getPlaceholder(sensor)}
                                            onChange={(e) => handleChange(sensor, e.target.value)}
                                            className={styles.configInput}
                                            style={{width: '100%'}}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button onClick={onClose} className={styles.tabButton}>Abbrechen</button>
                    <button onClick={handleSave} className={styles.downloadButton}>Speichern</button>
                </div>
            </div>
        </div>
    );
}

// --- UPDATED REPORT TABLE WITH RESIZING ---
function ReportTable({ data, title, sensorMappings, fullHeight = false, storageKey }) {
    const [sortConfig, setSortConfig] = useState(null);
    const [columnWidths, setColumnWidths] = useState({});

    // Load saved widths from LocalStorage
    useEffect(() => {
        if (storageKey) {
            const savedWidths = localStorage.getItem(`col_widths_${storageKey}`);
            if (savedWidths) {
                try {
                    setColumnWidths(JSON.parse(savedWidths));
                } catch (e) {
                    console.error("Failed to parse column widths", e);
                }
            }
        }
    }, [storageKey]);

    const saveWidths = useCallback((newWidths) => {
        if (storageKey) {
            localStorage.setItem(`col_widths_${storageKey}`, JSON.stringify(newWidths));
        }
    }, [storageKey]);

    const sortedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        let sortableData = [...data];
        if (sortConfig !== null) {
            sortableData.sort((a, b) => {
                if (sortConfig.key === 'report_date') {
                    const dateA = a.report_date instanceof Date ? a.report_date.getTime() : 0;
                    const dateB = b.report_date instanceof Date ? b.report_date.getTime() : 0;
                    return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
                }
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    const handleMouseDown = (headerKey, e) => {
        e.stopPropagation();
        e.preventDefault();

        const startX = e.pageX;
        // Determine start width: either from state or calculated from DOM
        const startWidth = columnWidths[headerKey] || e.target.closest('th').getBoundingClientRect().width;

        const onMouseMove = (moveEvent) => {
            const diffX = moveEvent.pageX - startX;
            const newWidth = Math.max(50, startWidth + diffX); // Minimum width 50px
            
            setColumnWidths(prev => ({
                ...prev,
                [headerKey]: newWidth
            }));
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            // Save the widths when dragging stops
            setColumnWidths(prev => {
                saveWidths(prev);
                return prev;
            });
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };
    
    if (!Array.isArray(data) || data.length === 0 || !data[0]) {
        return <p className={styles.noDataText}>Keine Daten für diese Ansicht verfügbar.</p>;
    }
    
    if (data.length === 1 && data[0].Status) {
        return <p className={styles.noDataText}>{data[0].Status}</p>;
    }
    
    const headers = Object.keys(data[0]).filter(key => key !== 'report_date' && isAllowedColumn(key));
    
    const formatHeader = (header) => {
        if (sensorMappings && sensorMappings[header]) {
            return sensorMappings[header];
        }
        if (STATIC_SENSOR_TRANSLATIONS[header]) {
            return STATIC_SENSOR_TRANSLATIONS[header];
        }
        return header.replace(/_/g, ' ');
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <div className={`${styles.tableContainer} ${fullHeight ? styles.tableContainerFull : ''}`}>
            {title && <h3 className={styles.tableTitle}>{title}</h3>}
            {/* tableLayout: fixed is crucial for width resizing to behave predictably */}
            <table className={styles.reportTable} style={{ tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        {headers.map(header => (
                            <th 
                                key={header} 
                                onClick={() => requestSort(header)} 
                                className={`${styles.sortableHeader} ${styles.resizableHeader}`}
                                style={{ 
                                    width: columnWidths[header] ? `${columnWidths[header]}px` : 'auto',
                                    minWidth: '80px',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {formatHeader(header)}
                                {sortConfig?.key === header && (
                                    <span>{sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽'}</span>
                                )}
                                <div 
                                    className={styles.resizer} 
                                    onMouseDown={(e) => handleMouseDown(header, e)}
                                    onClick={(e) => e.stopPropagation()} 
                                />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sortedData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {headers.map(header => (
                                <td 
                                    key={`${rowIndex}-${header}`}
                                    style={{
                                        width: columnWidths[header] ? `${columnWidths[header]}px` : 'auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    { (row[header] === null || row[header] === undefined) ? '' : String(row[header]) }
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ReportChart({ data, dateField, valueFields, sensorMappings }) {
    if (!Array.isArray(data) || data.length === 0) {
        return <p className={styles.noDataText}>Keine Daten für das Diagramm verfügbar.</p>;
    }
    
    if (!dateField || !Array.isArray(valueFields) || valueFields.length === 0) {
        return <p className={styles.noDataText}>Bitte wählen Sie Felder zum Anzeigen aus.</p>
    }

    const getLabel = (field) => {
        return sensorMappings?.[field] || STATIC_SENSOR_TRANSLATIONS[field] || field;
    };

    const cssVar = (name, fallback) => {
        if (typeof window === 'undefined') return fallback;
        const value = getComputedStyle(document.body).getPropertyValue(name)?.trim();
        return value || fallback;
    };

    const chartPalette = {
        accent: cssVar('--accent-color', '#172983'),
        accentStrong: cssVar('--accent-color-strong', '#2940b6'),
        success: cssVar('--success-color', '#058744'),
        successStrong: cssVar('--success-color-strong', '#046a37'),
        danger: cssVar('--danger-color', '#d14343'),
        textMuted: cssVar('--text-color-light', '#64748b'),
        border: cssVar('--border-color', '#cbd5e1')
    };

    const createGradient = (ctx, chartArea) => {
        if (!chartArea) return chartPalette.accent;
        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        gradient.addColorStop(0, `${chartPalette.accent}66`);
        gradient.addColorStop(1, chartPalette.accentStrong);
        return gradient;
    };
    
    const createBackgroundGradient = (ctx, chartArea) => {
        if (!chartArea) return `${chartPalette.accent}22`;
        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
        gradient.addColorStop(0, `${chartPalette.accent}14`);
        gradient.addColorStop(1, `${chartPalette.accentStrong}88`);
        return gradient;
    };
    
    const baseColors = [
        { border: chartPalette.accent, background: `${chartPalette.accent}22` },
        { border: chartPalette.success, background: `${chartPalette.success}22` },
        { border: chartPalette.danger, background: `${chartPalette.danger}22` },
        { border: chartPalette.accentStrong, background: `${chartPalette.accentStrong}22` },
        { border: chartPalette.successStrong, background: `${chartPalette.successStrong}22` },
    ];
    
    const datasets = valueFields.map((field, index) => {
        const color = baseColors[index % baseColors.length];
        const isFirstDataset = index === 0;

        return {
            label: getLabel(field),
            data: data.map(item => Number(item[field]) || 0),
            fill: valueFields.length === 1 ? true : (isFirstDataset ? 'origin' : false), 
            borderColor: (context) => {
                if (valueFields.length === 1) {
                    const { ctx, chartArea } = context.chart;
                    return createGradient(ctx, chartArea);
                }
                return color.border;
            },
            backgroundColor: (context) => {
                if (valueFields.length === 1) {
                    const { ctx, chartArea } = context.chart;
                    return createBackgroundGradient(ctx, chartArea);
                }
                return isFirstDataset ? color.background : 'transparent'; 
            },
            tension: 0.1,
        };
    });

    const chartData = {
        labels: data.map(item => {
            const dateVal = item[dateField];
            return dateVal instanceof Date ? dateVal.toLocaleDateString() : String(dateVal);
        }),
        datasets: datasets,
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                position: 'top',
                labels: { color: chartPalette.textMuted, usePointStyle: true }
            },
            title: { 
                display: true, 
                text: `Berichtsübersicht`,
                color: chartPalette.accent,
                font: { weight: '700', size: 16 }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `${context.dataset.label}: ${context.parsed.y}`;
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: { color: chartPalette.textMuted },
                grid: { color: chartPalette.border }
            },
            y: {
                ticks: { color: chartPalette.textMuted },
                grid: { color: chartPalette.border }
            }
        }
    };

    return <Line options={options} data={chartData} />;
}

function ReportHistoryModal({ isOpen, onClose, source, onDownload }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && source) {
            async function fetchHistory() {
                setLoading(true);
                const { data, error } = await supabase
                    .from('report_archive')
                    .select('storage_path, report_name, created_at')
                    .eq('report_source_id', source.id)
                    .order('created_at', { ascending: false });
                
                if (data) setHistory(data);
                if (error) alert("Verlauf konnte nicht geladen werden.");
                setLoading(false);
            }
            fetchHistory();
        }
    }, [isOpen, source]);

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2>Verlauf für: {source.source_name}</h2>
                <button onClick={onClose} className={styles.modalCloseButton}>
                    <CloseIcon fontSize="inherit" />
                </button>
                {loading ? (
                    <div className={styles.spinner}></div>
                ) : (
                    <ul className={styles.historyList}>
                        {history.length > 0 ? history.map(item => (
                            <li key={item.storage_path} className={styles.historyItem}>
                                <span>{item.report_name} ({new Date(item.created_at).toLocaleString()})</span>
                                <button onClick={() => onDownload(item.storage_path, item.report_name)} className={styles.downloadButtonSmall}>
                                    Download
                                </button>
                            </li>
                        )) : (
                            <p>Kein Verlauf für diesen Bericht gefunden.</p>
                        )}
                    </ul>
                )}
            </div>
        </div>
    );
}

function DashboardPage() {
    const { user, loading: authLoading, logout, plans, userProducts, refreshUserProducts } = useAuth();
    const { startTutorial, joyrideState, goToNextStep } = useTutorial();
    
    const [profile, setProfile] = useState(null);
    const [reportSources, setReportSources] = useState([]);
    
    const [activeSource, setActiveSource] = useState(null);
    const [activeTab, setActiveTab] = useState('daily');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewingReport, setViewingReport] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewingHistory, setViewingHistory] = useState(null);
    
    const [yAxisFields, setYAxisFields] = useState([]);
    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [sensorMappings, setSensorMappings] = useState({});
    const [isRenamingModalOpen, setRenamingModalOpen] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Fetch Mappings
    useEffect(() => {
        if (user?.id) {
            axios.get('/api/sensor-mappings', { params: { user_id: user.id } })
                .then(res => setSensorMappings(res.data))
                .catch(err => console.error("Failed to load sensor mappings", err));
        }
    }, [user]);

    // Save Mappings
    const handleSaveMappings = async (newMappings) => {
        if (!user?.id) return;
        try {
            await axios.post('/api/sensor-mappings', {
                user_id: user.id,
                mappings: newMappings
            });
            setSensorMappings(newMappings);
            setRenamingModalOpen(false);
        } catch (e) {
            alert("Fehler beim Speichern der Namen: " + (e.response?.data?.error || e.message));
        }
    };

    const processedDailyData = useMemo(() => {
        const dailyData = activeSource?.report_data?.daily;
        if (!Array.isArray(dailyData) || dailyData.length === 0) return [];
        const dateRegex = /_(\d{4})_(\d{2})_(\d{2})_/;
        const extractDate = (filename) => {
            if (typeof filename !== 'string') return filename;
            const match = filename.match(dateRegex);
            if (match) {
                return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            }
            return filename;
        };
        return dailyData
            .map(row => ({
                ...row,
                report_date: extractDate(row.Filename)
            }))
            .sort((a, b) => {
                const dateA = a.report_date instanceof Date ? a.report_date.getTime() : 0;
                const dateB = b.report_date instanceof Date ? b.report_date.getTime() : 0;
                return dateA - dateB;
            });
    }, [activeSource?.report_data?.daily]);
    
    const filteredDailyData = useMemo(() => {
        if (!startDate && !endDate) {
            return processedDailyData;
        }
        return processedDailyData.filter(item => {
            const itemDate = item.report_date;
            if (!(itemDate instanceof Date)) return true;
            const endFilterDate = endDate ? new Date(new Date(endDate).getTime() + 86400000) : null;
            if (startDate && itemDate < new Date(startDate)) return false;
            if (endFilterDate && itemDate >= endFilterDate) return false;
            return true;
        });
    }, [processedDailyData, startDate, endDate]);

    const numericHeaders = useMemo(() => {
        const dailyData = activeSource?.report_data?.daily;
        if (!Array.isArray(dailyData) || dailyData.length === 0) return [];
        const allHeaders = Object.keys(dailyData[0]);
        const stringFields = ['Filename', 'report_date']; 
        return allHeaders.filter(header => {
            if (stringFields.includes(header)) return false;
            if (!isAllowedColumn(header)) return false; // Filter calc columns
            const firstValue = dailyData.find(d => d[header] !== null)?.[header];
            return firstValue !== undefined && !isNaN(Number(firstValue));
        });
    }, [activeSource?.report_data?.daily]);
    
    const summaryStats = useMemo(() => {
        if (yAxisFields.length === 0 || filteredDailyData.length === 0) {
            return null;
        }
        const field = yAxisFields[0];
        const values = filteredDailyData
            .map(item => Number(item[field]))
            .filter(val => !isNaN(val));
        if (values.length === 0) return null;
        const sum = values.reduce((acc, val) => acc + val, 0);
        const avg = sum / values.length;
        const min = Math.min(...values);
        const max = Math.max(...values);
        return {
            field: sensorMappings[field] || STATIC_SENSOR_TRANSLATIONS[field] || field, // Use mapped name
            total: sum.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            avg: avg.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            min: min.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            max: max.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        };
    }, [filteredDailyData, yAxisFields, sensorMappings]);

    const isDashboardTutorialRunning = joyrideState.run && joyrideState.steps.some(step => step.target.startsWith('[data-tut="dashboard-'));
    const hasNoReports = reportSources.length === 0;

    useEffect(() => {
        if (authLoading || loading || joyrideState.run) {
            return;
        }
        
        const tutorialKey = 'tutorial_dashboard_completed';
        const hasCompleted = localStorage.getItem(tutorialKey);

        if (!hasCompleted) {
            startTutorial('dashboard', false); 
        }

    }, [authLoading, loading, startTutorial, joyrideState.run]);


    useEffect(() => {
        async function fetchData() {
            if (user?.id) {
                try {
                    const [profileData, reportsResponse] = await Promise.all([
                        getUserProfile(user.id),
                        supabase.from('user_reports').select('*').eq('user_id', user.id).order('last_updated', { ascending: false })
                    ]);
                    if (profileData) setProfile(profileData);
                    if (reportsResponse.data) setReportSources(reportsResponse.data);
                    if (reportsResponse.error) throw reportsResponse.error;
                } catch (err) {
                    setError('Dashboard-Daten konnten nicht geladen werden.');
                    console.error(err);
                }
            }
            setLoading(false);
        }
        if (!authLoading) fetchData();
    }, [user, authLoading]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('checkout') === 'success') {
            refreshUserProducts?.();
        }
    }, [location.search, refreshUserProducts]); 

    useEffect(() => {
        if (!joyrideState.run || !joyrideState.steps[joyrideState.stepIndex]) {
            return;
        }
        const currentTarget = joyrideState.steps[joyrideState.stepIndex].target;
        const isTutorialClickStep = currentTarget === '[data-tut="dashboard-source-item"]';

        if (isTutorialClickStep && activeSource !== null) {
            setTimeout(() => goToNextStep(), 400);
        }
    }, [activeSource, joyrideState.run, joyrideState.stepIndex, goToNextStep]);

    const handleExportExcel = async () => {
        if (!activeSource) {
            alert("Bitte wählen Sie eine Quelle zum Exportieren aus.");
            return;
        }

        try {
            const wb = XLSX.utils.book_new(); 
            let filename = `${activeSource.source_name}.xlsx`;

            // Helper to rename keys in an object based on mapping
            const renameRowKeys = (row) => {
                const newRow = {};
                Object.keys(row).forEach(key => {
                    if (isAllowedColumn(key)) {
                        const newKey = sensorMappings[key] || STATIC_SENSOR_TRANSLATIONS[key] || key;
                        newRow[newKey] = row[key];
                    }
                });
                return newRow;
            };

            // --- 1. PREPARE DATA ---
            if (activeTab === 'daily' || activeTab === 'overview') {
                if (filteredDailyData.length === 0) {
                    alert("Keine Daten im 'Täglichen Bericht' zum Exportieren.");
                    return;
                }
                
                const dataToExport = filteredDailyData.map(row => {
                    const rowWithRenamedKeys = renameRowKeys(row);
                    if (row.report_date instanceof Date) {
                         rowWithRenamedKeys['report_date'] = row.report_date.toISOString().split('T')[0];
                    }
                    return rowWithRenamedKeys;
                });

                const ws = XLSX.utils.json_to_sheet(dataToExport);
                XLSX.utils.book_append_sheet(wb, ws, 'Täglicher Bericht');
                filename = `${activeSource.source_name}_daily_report.xlsx`;

            } else if (activeTab === 'monthly') {
                if (!monthlyData || Object.keys(monthlyData).length === 0) {
                    alert("Keine Daten im 'Monatlichen Bericht' zum Exportieren.");
                    return;
                }
                
                Object.entries(monthlyData).forEach(([monthName, data]) => {
                    if (Array.isArray(data) && data.length > 0) {
                        const renamedData = data.map(row => renameRowKeys(row));
                        const ws = XLSX.utils.json_to_sheet(renamedData);
                        const sheetName = monthName.length > 31 ? monthName.substring(0, 31) : monthName;
                        XLSX.utils.book_append_sheet(wb, ws, sheetName);
                    }
                });
                filename = `${activeSource.source_name}_monthly_report.xlsx`;
            }

            // --- 2. ATTEMPT "SAVE AS" DIALOG ---
            if ('showSaveFilePicker' in window) {
                try {
                    const options = {
                        suggestedName: filename,
                        types: [
                            {
                                description: 'Excel Datei',
                                accept: {
                                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                                },
                            },
                        ],
                    };
                    
                    const fileHandle = await window.showSaveFilePicker(options);
                    const writableStream = await fileHandle.createWritable();
                    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                    await writableStream.write(wbout);
                    await writableStream.close();
                    return; 
                } catch (pickerError) {
                    if (pickerError.name === 'AbortError') return;
                    console.warn('File Picker failed, falling back:', pickerError);
                }
            }

            // --- 3. FALLBACK ---
            XLSX.writeFile(wb, filename);

        } catch (err) {
            console.error("Error generating Excel file:", err);
            alert(`Fehler beim Erstellen des Excel-Berichts: ${err.message}`);
        }
    };

    const handleDownloadArchive = async (storagePath, reportName) => {
        try {
            const { data, error } = await supabase.storage.from('reports').createSignedUrl(storagePath, 60);
            if (error) throw error;
            const link = document.createElement('a');
            link.href = data.signedUrl;
            link.download = reportName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            alert(`Fehler beim Herunterladen: ${err.message}`);
        }
    };

    const handleLogout = async () => { await logout(); navigate('/login'); };
    
    const handleSelectSource = (source) => {
        setActiveSource(source);
        setActiveTab('daily');
        setYAxisFields([]);
        setStartDate('');
        setEndDate('');
    };

    const handleRenameSource = async (e, source) => {
        e.stopPropagation();
        const newName = prompt("Neuen Namen eingeben für:", source.source_name);
        if (newName && newName.trim() !== "") {
            try {
                const { data, error } = await supabase
                    .from('user_reports')
                    .update({ source_name: newName.trim() })
                    .eq('id', source.id)
                    .select();
                if (error) throw error;
                if (data) {
                    setReportSources(reportSources.map(s => s.id === source.id ? data[0] : s));
                    if (activeSource?.id === source.id) {
                        setActiveSource(data[0]);
                    }
                }
            } catch (err) {
                alert(`Fehler beim Umbenennen: ${err.message}`);
            }
        }
    };

    const handleDeleteSource = async (e, source) => {
        e.stopPropagation();
        if (window.confirm(`Sind Sie sicher, dass Sie "${source.source_name}" löschen möchten? Dies kann nicht rückgängig gemacht werden.`)) {
            try {
                const { error } = await supabase
                    .from('user_reports')
                    .delete()
                    .eq('id', source.id);
                if (error) throw error;
                setReportSources(reportSources.filter(s => s.id !== source.id));
                if (activeSource?.id === source.id) {
                    setActiveSource(null);
                }
            } catch (err) {
                alert(`Fehler beim Löschen: ${err.message}`);
            }
        }
    };
    
    const handleViewHistory = (e, source) => { e.stopPropagation(); setViewingHistory(source); };
    
    const handleYAxisToggle = (field) => {
        setYAxisFields(prev => {
            if (prev.includes(field)) {
                return prev.filter(f => f !== field);
            } else {
                return [...prev, field];
            }
        });
    };
    
    if (authLoading || loading) {
        return <div className={styles.dashboard}><div className={styles.spinner}></div></div>;
    }
    if (error) {
        return <div className={styles.dashboard}><p className={styles.errorText}>{error}</p></div>;
    }

    const monthlyData = activeSource?.report_data?.monthly;
    const filteredSources = reportSources.filter(source =>
        source.source_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return (
        <div className={styles.dashboard}>
            <SensorRenamingModal 
                isOpen={isRenamingModalOpen}
                onClose={() => setRenamingModalOpen(false)}
                currentData={activeSource?.report_data}
                mappings={sensorMappings}
                onSave={handleSaveMappings}
            />

            <ReportViewerModal 
                isOpen={!!viewingReport} 
                onClose={() => setViewingReport(null)} 
                report={viewingReport} 
            />
            <ReportHistoryModal
                isOpen={!!viewingHistory}
                onClose={() => setViewingHistory(null)}
                source={viewingHistory}
                onDownload={handleDownloadArchive}
            />

            <div className={styles.headerRow}>
                <h1 className={styles.welcomeHeader}>Willkommen zurück{profile?.full_name ? `, ${profile.full_name}` : ''}!</h1>
                <button className={styles.logoutButton} onClick={handleLogout}>Abmelden</button>
            </div>
            
            {(!(userProducts?.some(p => p.product_name === 'anomaly' && p.is_active))) && (
                <div className={styles.upgradeSection}>
                    <UpgradePrompt product="chatbot" />
                    <UpgradePrompt product="anomaly" />
                </div>
            )}
            
            <div className={`${styles.dashboardLayout} ${!isSidebarOpen ? styles.sidebarClosed : ''}`}>
                <div className={styles.leftColumn}>
                    <div className={styles.sourceListPanel} data-tut="dashboard-source-list">
                        <div className={styles.sidebarToggleHeader}>
                            <h2>Berichtsquellen</h2>
                            <button 
                                onClick={() => setSidebarOpen(!isSidebarOpen)} 
                                className={styles.sidebarToggleButton}
                                title={isSidebarOpen ? "Seitenleiste einklappen" : "Seitenleiste ausklappen"}
                                data-tut="dashboard-sidebar-toggle" 
                            >
                                {isSidebarOpen ? <ChevronLeftIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
                            </button>
                        </div>
                        
                        <input
                            type="text"
                            placeholder="Berichte suchen..."
                            className={styles.searchInput}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        
                        <ul className={styles.sourceList}>
                            {hasNoReports && isDashboardTutorialRunning ? (
                                <li 
                                    className={`${styles.sourceItem} ${activeSource?.id === 'tutorial-id' ? styles.activeItem : ''}`}
                                    data-tut="dashboard-source-item"
                                    onClick={() => handleSelectSource({
                                        id: 'tutorial-id',
                                        source_name: 'Beispielbericht (Tutorial)',
                                        last_updated: new Date().toISOString(),
                                        report_data: { 
                                            daily: [{ Filename: 'tutorial_file.csv', report_date: new Date(), 'Beispiel-Daten': 100, 'Anderer-Wert': 50 }], 
                                            monthly: { 'Beispiel-Monat': [{ 'Monatliche-Daten': 500 }] }
                                        } 
                                    })}
                                >
                                    <div className={styles.sourceInfo}>
                                        <span className={styles.sourceName}>Beispielbericht (Tutorial)</span>
                                        <span className={styles.sourceDate}>Zuletzt aktualisiert: --/--/----</span>
                                    </div>
                                    <div className={styles.sourceActions}>
                                        <button style={{pointerEvents: 'none'}} title="Verlauf anzeigen" data-tut="dashboard-source-history">
                                            <HistoryIcon fontSize="inherit" />
                                        </button>
                                        <button style={{pointerEvents: 'none'}} title="Umbenennen" data-tut="dashboard-source-rename">
                                            <EditIcon fontSize="inherit" />
                                        </button>
                                        <button style={{pointerEvents: 'none'}} title="Löschen" data-tut="dashboard-source-delete">
                                            <DeleteIcon fontSize="inherit" />
                                        </button>
                                    </div>
                                </li>
                            ) : (
                                filteredSources.map((source, index) => (
                                    <li key={source.id} 
                                        className={`${styles.sourceItem} ${activeSource?.id === source.id ? styles.activeItem : ''}`}
                                        onClick={() => handleSelectSource(source)} 
                                        data-tut={index === 0 ? "dashboard-source-item" : undefined}
                                    >
                                        <div className={styles.sourceInfo}>
                                            <span className={styles.sourceName}>{source.source_name}</span>
                                            <span className={styles.sourceDate}>Zuletzt aktualisiert: {new Date(source.last_updated).toLocaleDateString()}</span>
                                        </div>
                                        <div className={styles.sourceActions}>
                                            <button onClick={(e) => handleViewHistory(e, source)} title="Verlauf anzeigen" data-tut={index === 0 ? "dashboard-source-history" : undefined}>
                                                <HistoryIcon fontSize="inherit" />
                                            </button>
                                            <button onClick={(e) => handleRenameSource(e, source)} title="Umbenennen" data-tut={index === 0 ? "dashboard-source-rename" : undefined}>
                                                <EditIcon fontSize="inherit" />
                                            </button>
                                            <button onClick={(e) => handleDeleteSource(e, source)} title="Löschen" data-tut={index === 0 ? "dashboard-source-delete" : undefined}>
                                                <DeleteIcon fontSize="inherit" />
                                            </button>
                                        </div>
                                    </li>
                                ))
                            )}
                        </ul>
                        {reportSources.length === 0 && !isDashboardTutorialRunning && (
                            <p className={styles.noDataText}>Sie haben keine gespeicherten Berichtsquellen.<br/><a href="/anomaly-detection">Analysieren Sie eine Datei, um zu beginnen.</a></p>
                        )}
                    </div>
                </div>

                <div className={styles.reportViewer} data-tut="dashboard-report-viewer">
                     {!isSidebarOpen && (
                        <button 
                            onClick={() => setSidebarOpen(true)} 
                            className={styles.sidebarToggleButtonFloating}
                            title="Seitenleiste ausklappen"
                        >
                            <ChevronRightIcon fontSize="inherit" />
                        </button>
                    )}

                    {activeSource ? (
                        <div className={styles.reportContainer}>
                            <div className={styles.reportHeader}>
                                <div className={styles.tabContainer} data-tut="dashboard-tabs">
                                    <button 
                                        className={`${styles.tabButton} ${activeTab === 'daily' ? styles.activeTab : ''}`} 
                                        onClick={() => setActiveTab('daily')}
                                        data-tut="dashboard-tab-daily"
                                    >
                                        Täglicher Bericht
                                    </button>
                                    <button 
                                        className={`${styles.tabButton} ${activeTab === 'monthly' ? styles.activeTab : ''}`} 
                                        onClick={() => setActiveTab('monthly')}
                                        data-tut="dashboard-tab-monthly"
                                    >
                                        Monatlicher Bericht
                                    </button>
                                    <button 
                                        className={`${styles.tabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`} 
                                        onClick={() => setActiveTab('overview')}
                                        data-tut="dashboard-tab-overview"
                                    >
                                        Übersicht
                                    </button>
                                </div>
                                
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <button 
                                        className={styles.tabButton} 
                                        onClick={() => setRenamingModalOpen(true)}
                                        title="Sensoren umbenennen"
                                    >
                                        <SettingsIcon style={{ verticalAlign: 'middle', marginRight: '4px' }} fontSize="small"/>
                                        Sensoren
                                    </button>

                                    {activeTab === 'overview' && (
                                        <div className={styles.dateFilterContainer}>
                                            <label htmlFor="start-date">Von:</label>
                                            <input 
                                                type="date" 
                                                id="start-date"
                                                className={styles.dateInput}
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                            <label htmlFor="end-date">Bis:</label>
                                            <input 
                                                type="date" 
                                                id="end-date"
                                                className={styles.dateInput}
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </div>
                                    )}
                                    
                                    <button className={styles.downloadButton} onClick={handleExportExcel} data-tut="dashboard-download-button">
                                        Bericht herunterladen
                                    </button>
                                </div>
                            </div>
                            
                            <div className={styles.reportContent}>
                                {activeTab === 'daily' && (
                                    <div data-tut="dashboard-daily-table">
                                        <ReportTable 
                                            data={filteredDailyData} 
                                            sensorMappings={sensorMappings} 
                                            storageKey={`${activeSource.id}_daily`}
                                        />
                                    </div>
                                )}

                                {activeTab === 'monthly' && (
                                    monthlyData && Object.keys(monthlyData).length > 0 ? (
                                        Object.entries(monthlyData).map(([filename, data]) => (
                                            <ReportTable 
                                                key={filename} 
                                                data={data} 
                                                title={`Monat: ${filename}`}
                                                sensorMappings={sensorMappings} 
                                                fullHeight={true} 
                                                storageKey={`${activeSource.id}_monthly_${filename}`}
                                            />
                                        ))
                                    ) : (
                                        <p className={styles.noDataText}>Keine monatlichen Daten verfügbar.</p>
                                    )
                                )}
                                {activeTab === 'overview' && (
                                    <>
                                        {summaryStats && (
                                            <div className={styles.kpiWidgetContainer}>
                                                <div className={styles.widget}>
                                                    <h3>Gesamt ({summaryStats.field})</h3>
                                                    <p className={styles.widgetValue}>{summaryStats.total}</p>
                                                </div>
                                                <div className={styles.widget}>
                                                    <h3>Durchschnitt ({summaryStats.field})</h3>
                                                    <p className={styles.widgetValue}>{summaryStats.avg}</p>
                                                </div>
                                                <div className={styles.widget}>
                                                    <h3>Min ({summaryStats.field})</h3>
                                                    <p className={styles.widgetValue}>{summaryStats.min}</p>
                                                </div>
                                                <div className={styles.widget}>
                                                    <h3>Max ({summaryStats.field})</h3>
                                                    <p className={styles.widgetValue}>{summaryStats.max}</p>
                                                </div>
                                            </div>
                                        )}
                                    
                                        <div className={styles.chartControls}>
                                            <div className={styles.chartControlItem}>
                                                <label>Metriken für Y-Achse wählen:</label>
                                                <div className={styles.checkboxContainer}>
                                                    {numericHeaders.length > 0 ? numericHeaders.map(header => (
                                                        <label key={header} className={styles.checkboxLabel}>
                                                            <input
                                                                type="checkbox"
                                                                value={header}
                                                                checked={yAxisFields.includes(header)}
                                                                onChange={() => handleYAxisToggle(header)}
                                                            />
                                                            {sensorMappings[header] || STATIC_SENSOR_TRANSLATIONS[header] || header}
                                                        </label>
                                                    )) : (
                                                        <p className={styles.noDataTextSmall}>Keine numerischen Datenfelder gefunden.</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {yAxisFields.length === 0 ? (
                                            <p className={styles.noDataText}>Bitte wählen Sie eine Y-Achse (Wert), um das Diagramm anzuzeigen.</p>
                                        ) : (
                                            <div className={styles.chartContainer}>
                                                <ReportChart 
                                                    data={filteredDailyData} 
                                                    dateField="report_date"
                                                    valueFields={yAxisFields} 
                                                    sensorMappings={sensorMappings}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={styles.placeholder}>
                            <h2>Globales Dashboard</h2>
                            <p>Wählen Sie links einen Bericht aus, um Details anzuzeigen, oder sehen Sie unten Ihre globale Zusammenfassung.</p>
                            <div className={styles.widgetContainer}>
                                <div className={styles.widget}>
                                    <h3>Berichte Gesamt</h3>
                                    <p className={styles.widgetValue}>{reportSources.length}</p>
                                </div>
                                <div className={styles.widget}>
                                    <h3>Neueste Berichte</h3>
                                    <ul className={styles.widgetList}>
                                        {reportSources.slice(0, 3).map(s => <li key={s.id}>{s.source_name}</li>)}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DashboardPage;